import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../../apiClient/apiClient';
import Navbar from '../Navbar/Navbar';
import Footer from '../Footer';
import Shimmer from '../../component/Shimmer';
import { 
  FaCalendarAlt, 
  FaMapMarkerAlt, 
  FaPhone, 
  FaEnvelope, 
  FaInfoCircle, 
  FaBuilding,
  FaGlobe,
  FaUsers,
  FaTicketAlt,
  FaClock,
  FaArrowLeft,
  FaExternalLinkAlt,
  FaChartLine,
  FaStar,
  FaHeart
} from 'react-icons/fa';
import './OrganizerDetail.css';

const OrganizerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [organizer, setOrganizer] = useState(null);
  const [organizerEvents, setOrganizerEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [eventStats, setEventStats] = useState(null);

  useEffect(() => {
    const fetchOrganizerDetails = async () => {
      setLoading(true);
      setError(null); // Reset error state
      
      try {
        // Try to fetch organizer profile directly first
        let organizerData = null;
        
        try {
          // Make request without authentication headers for public access
          const profileResponse = await apiClient.get(`api/organizer/${id}/`);
          organizerData = profileResponse.data;
          console.log('Fetched organizer profile:', organizerData);
        } catch (profileError) {
          console.log('Could not fetch organizer profile directly:', profileError.response?.status);
          // Don't set error here, we'll try to get data from events
        }
        
        // Fetch events by this organizer (this should also be public)
        let events = [];
        try {
        const eventsResponse = await apiClient.get(`api/events/organizer/${id}/`);
          events = eventsResponse.data.results || [];
        } catch (eventsError) {
          console.log('Could not fetch organizer events:', eventsError.response?.status);
          // If we can't get events either, this might be a real error
          if (!organizerData) {
            throw new Error('Unable to load organizer information. This organizer may not exist or may not have any public events.');
          }
        }
        
        // Calculate event statistics
        const stats = calculateEventStats(events);
        setEventStats(stats);
          
        // If we couldn't get the profile directly, extract from first event
        if (!organizerData && events.length > 0) {
          const firstEvent = events[0];
          organizerData = {
            id: id,
            company_name: firstEvent.organizer_company || "Event Organizer",
            company_logo: firstEvent.organizer_logo,
            description: firstEvent.organizer_description || "Professional event organizer creating memorable experiences",
            business_email: firstEvent.organizer_business_email,
            business_phone: firstEvent.organizer_business_phone
          };
        } else if (!organizerData && events.length === 0) {
          // No profile and no events found - organizer might not exist
          throw new Error('Organizer not found or has no public information available.');
        }
        
        setOrganizer(organizerData);
        setOrganizerEvents(events);
        setLoading(false);
        
      } catch (err) {
        console.error('Error fetching organizer details:', err);
        setError(err.message || 'Failed to load organizer details. Please try again later.');
        setLoading(false);
      }
    };

    if (id) {
      fetchOrganizerDetails();
    }
  }, [id]);

  const calculateEventStats = (events) => {
    const now = new Date();
    const upcomingEvents = events.filter(event => new Date(event.start_date) > now);
    const pastEvents = events.filter(event => new Date(event.start_date) <= now);
    
    // Get unique categories
    const categories = [...new Set(events.map(event => event.category_name).filter(Boolean))];
    
    // Calculate average price range
    const pricesWithRanges = events.filter(event => event.min_ticket_price || event.max_ticket_price);
    
    return {
      totalEvents: events.length,
      upcomingEvents: upcomingEvents.length,
      pastEvents: pastEvents.length,
      categories: categories.length,
      topCategories: categories.slice(0, 3),
      hasTicketInfo: pricesWithRanges.length > 0,
      averagePrice: pricesWithRanges.length > 0 ? 
        (pricesWithRanges.reduce((sum, event) => sum + (parseFloat(event.min_ticket_price) || 0), 0) / pricesWithRanges.length).toFixed(2) : 
        null
    };
  };

  const formatEventDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const isUpcomingEvent = (dateString) => {
    return new Date(dateString) > new Date();
  };

  if (error) {
    return (
      <div className="flex flex-col min-h-screen">
        <Navbar />
        <div className="flex-grow flex items-center justify-center bg-gray-50">
          <div className="text-center p-8 max-w-md bg-white rounded-lg shadow-lg">
            <FaInfoCircle className="mx-auto text-red-500 text-5xl mb-4" />
            <h2 className="text-2xl font-bold mb-2 text-gray-800">Oops! Something went wrong</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button 
              onClick={() => navigate(-1)}
              className="bg-sunday_primary hover:bg-yellow-500 text-white px-6 py-2 rounded-lg transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="flex-grow">
        {loading ? (
          <div className="container mx-auto px-4 py-8">
          <div className="space-y-8">
            {/* Loading state */}
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <Shimmer width="300px" height="40px" borderRadius="rounded" />
                <div className="flex flex-col lg:flex-row mt-8 gap-8">
                  <Shimmer width="200px" height="200px" borderRadius="rounded-full" />
                  <div className="flex-1 space-y-6">
                  <Shimmer width="100%" height="24px" borderRadius="rounded" />
                  <Shimmer width="80%" height="24px" borderRadius="rounded" />
                  <Shimmer width="60%" height="24px" borderRadius="rounded" />
                    <div className="grid grid-cols-2 gap-4 mt-6">
                      <Shimmer width="100%" height="60px" borderRadius="rounded" />
                      <Shimmer width="100%" height="60px" borderRadius="rounded" />
                    </div>
                </div>
              </div>
            </div>
            
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {[...Array(4)].map((_, index) => (
                  <Shimmer key={index} width="100%" height="120px" borderRadius="rounded-lg" />
                ))}
              </div>
              
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <Shimmer width="250px" height="32px" borderRadius="rounded" className="mb-6" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[...Array(6)].map((_, index) => (
                    <Shimmer key={index} width="100%" height="280px" borderRadius="rounded-lg" />
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Hero Section */}
            <div className="bg-gradient-to-br from-sunday_info to-blue-600 text-white">
              <div className="container mx-auto px-4 py-12">
                <button 
                  onClick={() => navigate(-1)}
                  className="flex items-center text-white/80 hover:text-white mb-6 transition-colors"
                >
                  <FaArrowLeft className="mr-2" />
                  Back
                </button>
              
                <div className="flex flex-col lg:flex-row items-start lg:items-center gap-8">
                  {/* Organizer Logo */}
                  <div className="flex-shrink-0">
                    <div className="w-32 h-32 lg:w-40 lg:h-40 rounded-full overflow-hidden border-4 border-white/20 bg-white/10 flex items-center justify-center">
                      {organizer?.company_logo ? (
                        <img 
                          src={organizer.company_logo} 
                          alt={`${organizer.company_name} logo`}
                      className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className="w-full h-full flex items-center justify-center"
                        style={{ display: organizer?.company_logo ? 'none' : 'flex' }}
                      >
                        <div className="text-center">
                          <FaBuilding className="text-4xl mb-2 text-white/60" />
                          <span className="text-sm font-medium text-white/80">
                            {organizer?.company_name?.charAt(0) || 'O'}
                          </span>
                        </div>
                  </div>
                </div>
                  </div>
                  
                  {/* Organizer Info */}
                  <div className="flex-1">
                    <h1 className="text-4xl lg:text-5xl font-bold mb-4">
                      {organizer?.company_name || 'Event Organizer'}
                    </h1>
                    <p className="text-xl text-white/90 mb-6 leading-relaxed">
                      {organizer?.description || 'Professional event organizer creating memorable experiences'}
                    </p>
                    
                    {/* Contact Info */}
                    <div className="flex flex-wrap gap-6">
                      {organizer?.business_email && (
                        <div className="flex items-center text-white/80">
                          <FaEnvelope className="mr-2" />
                          <span>{organizer.business_email}</span>
                        </div>
                      )}
                      {organizer?.business_phone && (
                        <div className="flex items-center text-white/80">
                          <FaPhone className="mr-2" />
                          <span>{organizer.business_phone}</span>
                      </div>
                    )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Stats Section */}
            {eventStats && (
              <div className="container mx-auto px-4 -mt-8 relative z-10">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl p-6 shadow-lg text-center">
                    <FaCalendarAlt className="text-3xl text-sunday_primary mx-auto mb-3" />
                    <div className="text-2xl font-bold text-gray-800">{eventStats.totalEvents}</div>
                    <div className="text-sm text-gray-600">Total Events</div>
                  </div>
                  
                  <div className="bg-white rounded-xl p-6 shadow-lg text-center">
                    <FaClock className="text-3xl text-green-500 mx-auto mb-3" />
                    <div className="text-2xl font-bold text-gray-800">{eventStats.upcomingEvents}</div>
                    <div className="text-sm text-gray-600">Upcoming Events</div>
                  </div>
                  
                  <div className="bg-white rounded-xl p-6 shadow-lg text-center">
                    <FaUsers className="text-3xl text-purple-500 mx-auto mb-3" />
                    <div className="text-2xl font-bold text-gray-800">{eventStats.categories}</div>
                    <div className="text-sm text-gray-600">Event Categories</div>
                  </div>
                  
                  <div className="bg-white rounded-xl p-6 shadow-lg text-center">
                    <FaChartLine className="text-3xl text-blue-500 mx-auto mb-3" />
                    <div className="text-2xl font-bold text-gray-800">{eventStats.pastEvents}</div>
                    <div className="text-sm text-gray-600">Past Events</div>
                  </div>
                </div>
              </div>
            )}

            {/* Main Content */}
            <div className="container mx-auto px-4 py-12">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* About Section */}
                <div className="lg:col-span-2">
                  <div className="bg-white rounded-xl p-8 shadow-lg mb-8">
                    <h2 className="text-2xl font-bold mb-6 flex items-center text-gray-800">
                      <FaInfoCircle className="mr-3 text-sunday_primary" />
                      About {organizer?.company_name}
                    </h2>
                    
                    <div className="prose max-w-none">
                      <p className="text-gray-700 leading-relaxed text-lg">
                        {organizer?.description || 'This organizer is dedicated to creating exceptional events and memorable experiences for their community.'}
                      </p>
                    </div>

                    {/* Categories */}
                    {eventStats?.topCategories && eventStats.topCategories.length > 0 && (
                      <div className="mt-8">
                        <h3 className="text-lg font-semibold mb-4 text-gray-800">Specializes in:</h3>
                        <div className="flex flex-wrap gap-2">
                          {eventStats.topCategories.map((category, index) => (
                            <span 
                              key={index}
                              className="px-4 py-2 bg-sunday_primary/10 text-sunday_primary rounded-full text-sm font-medium"
                            >
                              {category}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Events Section */}
                  <div className="bg-white rounded-xl p-8 shadow-lg">
                    <div className="flex items-center justify-between mb-8">
                      <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                        <FaTicketAlt className="mr-3 text-sunday_primary" />
                        Events by {organizer?.company_name}
              </h2>
                      
                      {organizerEvents.length > 6 && (
                        <button className="text-sunday_primary hover:text-yellow-600 font-medium">
                          View All Events
                        </button>
                      )}
                    </div>
              
              {organizerEvents.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {organizerEvents.slice(0, 6).map(event => (
                          <div 
                      key={event.event_id} 
                            className="group cursor-pointer"
                            onClick={() => navigate(`/event/${event.event_id}`)}
                    >
                            <div className="bg-gray-50 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300 border border-gray-100 group-hover:border-sunday_primary/30">
                              <div className="relative h-48 overflow-hidden">
                        <img 
                          src={event.thumbnail_path || 'https://via.placeholder.com/400x200'} 
                          alt={event.event_name}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                                {isUpcomingEvent(event.start_date) && (
                                  <div className="absolute top-3 left-3 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                                    Upcoming
                                  </div>
                                )}
                                {event.event_type === 'External' && (
                                  <div className="absolute top-3 right-3 bg-blue-500 text-white p-2 rounded-full">
                                    <FaExternalLinkAlt className="text-xs" />
                                  </div>
                                )}
                              </div>
                              <div className="p-6">
                                <h3 className="font-bold text-lg mb-3 text-gray-800 group-hover:text-sunday_primary transition-colors line-clamp-2">
                                  {event.event_name}
                                </h3>
                                
                                <div className="space-y-2 mb-4">
                                  <div className="flex items-center text-sm text-gray-600">
                                    <FaCalendarAlt className="mr-2 text-sunday_primary" />
                                    <span>{formatEventDate(event.start_date)}</span>
                                  </div>
                                  <div className="flex items-center text-sm text-gray-600">
                                    <FaMapMarkerAlt className="mr-2 text-sunday_primary" />
                                    <span className="line-clamp-1">
                                      {event.venue_name || event.venue_address || 'Location TBA'}
                                    </span>
                                  </div>
                                  {event.price_range && (
                                    <div className="flex items-center text-sm text-gray-600">
                                      <FaTicketAlt className="mr-2 text-sunday_primary" />
                                      <span>{event.price_range}</span>
                                    </div>
                                  )}
                                </div>

                                {event.category_name && (
                                  <div className="flex justify-between items-center">
                                    <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">
                                      {event.category_name}
                                    </span>
                                    <div className="text-sunday_primary group-hover:translate-x-1 transition-transform">
                                      →
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <FaCalendarAlt className="text-6xl text-gray-300 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-gray-600 mb-2">No Events Yet</h3>
                        <p className="text-gray-500">This organizer hasn't published any events yet. Check back soon!</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                  {/* Contact Card */}
                  <div className="bg-white rounded-xl p-6 shadow-lg">
                    <h3 className="text-xl font-bold mb-4 text-gray-800">Get in Touch</h3>
                    
                    <div className="space-y-4">
                      {organizer?.business_email && (
                        <div className="flex items-start">
                          <FaEnvelope className="text-sunday_primary mr-3 mt-1" />
                          <div>
                            <div className="text-sm text-gray-500">Email</div>
                            <a 
                              href={`mailto:${organizer.business_email}`}
                              className="text-gray-800 hover:text-sunday_primary transition-colors"
                            >
                              {organizer.business_email}
                            </a>
                          </div>
                        </div>
                      )}
                      
                      {organizer?.business_phone && (
                        <div className="flex items-start">
                          <FaPhone className="text-sunday_primary mr-3 mt-1" />
                          <div>
                            <div className="text-sm text-gray-500">Phone</div>
                            <a 
                              href={`tel:${organizer.business_phone}`}
                              className="text-gray-800 hover:text-sunday_primary transition-colors"
                            >
                              {organizer.business_phone}
                            </a>
                          </div>
                        </div>
                      )}

                      {organizer?.city_town && (
                        <div className="flex items-start">
                          <FaMapMarkerAlt className="text-sunday_primary mr-3 mt-1" />
                          <div>
                            <div className="text-sm text-gray-500">Location</div>
                            <div className="text-gray-800">
                              {organizer.city_town}
                              {organizer.county && `, ${organizer.county}`}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Quick Stats */}
                  {eventStats && (
                    <div className="bg-white rounded-xl p-6 shadow-lg">
                      <h3 className="text-xl font-bold mb-4 text-gray-800">Quick Stats</h3>
                      
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Total Events</span>
                          <span className="font-semibold text-gray-800">{eventStats.totalEvents}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Upcoming</span>
                          <span className="font-semibold text-green-600">{eventStats.upcomingEvents}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Categories</span>
                          <span className="font-semibold text-gray-800">{eventStats.categories}</span>
                        </div>
                        {eventStats.averagePrice && (
                          <div className="flex justify-between items-center pt-2 border-t">
                            <span className="text-gray-600">Avg. Ticket Price</span>
                            <span className="font-semibold text-sunday_primary">£{eventStats.averagePrice}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Trust Indicators */}
                  <div className="bg-gradient-to-r from-sunday_primary to-yellow-400 text-white rounded-xl p-6">
                    <h3 className="text-lg font-bold mb-4">Verified Organizer</h3>
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <FaStar className="mr-2" />
                        <span className="text-sm">Verified Business</span>
                      </div>
                      <div className="flex items-center">
                        <FaHeart className="mr-2" />
                        <span className="text-sm">Trusted by Community</span>
                      </div>
                      <div className="flex items-center">
                        <FaTicketAlt className="mr-2" />
                        <span className="text-sm">Secure Booking</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      
      <Footer />
    </div>
  );
};

export default OrganizerDetail; 